import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  FilterList,
  Visibility,
  Download,
  Refresh,
  ArrowBack
} from '@mui/icons-material';
import api from '../../services/api';

const KarigarList = () => {
  const [karigars, setKarigars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedKarigar, setSelectedKarigar] = useState(null);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  // Controlled form fields
  const [form, setForm] = useState({
    name: '',
    abbreviation: '',
    module: 'karigar',
    phone: '',
    address: '',
    type: 'Gold',
    status: 'Active',
    specialization: '',
    experience: ''
  });

  // Fetch karigars from backend
  useEffect(() => {
    const fetchKarigars = async () => {
      setLoading(true);
      try {
        const res = await api.get('/api/karigar');
        setKarigars(res.data);
      } catch (err) {
        setError('Failed to fetch karigars');
      }
      setLoading(false);
    };
    fetchKarigars();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleFilterType = (event) => {
    setFilterType(event.target.value);
    setPage(0);
  };

  const handleFilterStatus = (event) => {
    setFilterStatus(event.target.value);
    setPage(0);
  };

  const handleEdit = (karigar) => {
    setSelectedKarigar(karigar);
    setForm({
      name: karigar.name || '',
      abbreviation: karigar.abbreviation || '',
      module: karigar.module || 'karigar',
      phone: karigar.phone || '',
      address: karigar.address || '',
      type: karigar.type || 'Gold',
      status: karigar.status || 'Active',
      specialization: karigar.specialization || '',
      experience: karigar.experience || ''
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this karigar?')) {
      try {
        await api.delete(`/api/karigar/${id}`);
        setKarigars(karigars.filter(k => k._id !== id));
      } catch (err) {
        setError('Failed to delete karigar');
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedKarigar(null);
    setForm({
      name: '',
      abbreviation: '',
      module: 'karigar',
      phone: '',
      address: '',
      type: 'Gold',
      status: 'Active',
      specialization: '',
      experience: ''
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    // Frontend validation for required fields (no empty or whitespace-only values)
    const requiredFields = ['name', 'abbreviation', 'module', 'address', 'type', 'status', 'specialization', 'experience'];
    for (const field of requiredFields) {
      if (!form[field] || form[field].toString().trim() === '') {
        setFormError(`Please fill out the ${field.charAt(0).toUpperCase() + field.slice(1)} field.`);
        return;
      }
    }
    setFormError('');
    try {
      const payload = {
        ...form,
        rating: 0,
        completedOrders: 0,
        totalOrders: 0
      };
      console.log('Submitting payload:', payload);
      if (selectedKarigar) {
        // Update
        const res = await api.put(`/api/karigar/${selectedKarigar._id}`, payload);
        setKarigars(karigars.map(k => k._id === selectedKarigar._id ? res.data.karigar : k));
      } else {
        // Add
        const res = await api.post('/api/karigar', payload);
        setKarigars([...karigars, res.data.karigar]);
      }
      handleCloseDialog();
    } catch (err) {
      setError('Failed to save karigar');
    }
  };

  const filteredKarigars = karigars.filter(karigar => {
    const matchesSearch = (karigar.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (karigar.phone || '').includes(searchTerm) ||
                         (karigar.specialization || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || karigar.type === filterType;
    const matchesStatus = filterStatus === 'all' || karigar.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const paginatedKarigars = filteredKarigars.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getStatusColor = (status) => {
    return status === 'Active' ? 'success' : 'error';
  };

  const getTypeColor = (type) => {
    return type === 'Gold' ? 'warning' : 'default';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Loading karigars...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ py: 4, px: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => window.history.back()} variant="outlined" color="primary">
          Back
        </Button>
      </Box>
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Karigar Directory
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and view all registered karigars
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary">
                {karigars.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Karigars
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="success.main">
                {karigars.filter(k => k.status === 'Active').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Karigars
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="warning.main">
                {karigars.filter(k => k.type === 'Gold').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gold Karigars
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="info.main">
                {karigars.filter(k => k.type === 'Silver').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Silver Karigars
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Bar */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by name, phone, or specialization..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select value={filterType} onChange={handleFilterType} label="Type">
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="Gold">Gold</MenuItem>
                <MenuItem value="Silver">Silver</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} onChange={handleFilterStatus} label="Status">
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setOpenDialog(true)}
              >
                Add Karigar
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
              >
                Export
              </Button>
              <IconButton onClick={() => window.location.reload()}>
                <Refresh />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Karigar Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Specialization</TableCell>
                <TableCell>Experience</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedKarigars.map((karigar) => (
                <TableRow key={karigar._id || karigar.id}>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {karigar.name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{karigar.phone || '-'}</TableCell>
                  <TableCell>{karigar.address || '-'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={karigar.type || '-'} 
                      color={getTypeColor(karigar.type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={karigar.status || '-'} 
                      color={getStatusColor(karigar.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{karigar.specialization || '-'}</TableCell>
                  <TableCell>{karigar.experience || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => handleEdit(karigar)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(karigar._id || karigar.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredKarigars.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Edit/Add Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedKarigar ? 'Edit Karigar' : 'Add New Karigar'}
        </DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={form.name}
                onChange={handleFormChange}
                required
                placeholder="Enter full name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Abbreviation"
                name="abbreviation"
                value={form.abbreviation}
                onChange={handleFormChange}
                required
                inputProps={{ maxLength: 5 }}
                helperText="Short unique code (max 5 chars)"
                placeholder="e.g. SN"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Module</InputLabel>
                <Select
                  name="module"
                  value={form.module}
                  label="Module"
                  onChange={handleFormChange}
                  required
                >
                  <MenuItem value="karigar">Goldsmith</MenuItem>
                  <MenuItem value="shop">Shop</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={form.phone}
                onChange={handleFormChange}
                required
                placeholder="Enter phone number"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                multiline
                rows={2}
                value={form.address}
                onChange={handleFormChange}
                required
                placeholder="Enter address"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={form.type}
                  label="Type"
                  onChange={handleFormChange}
                  required
                >
                  <MenuItem value="Gold">Gold</MenuItem>
                  <MenuItem value="Silver">Silver</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={form.status}
                  label="Status"
                  onChange={handleFormChange}
                  required
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Specialization"
                name="specialization"
                value={form.specialization}
                onChange={handleFormChange}
                required
                placeholder="e.g. Ring Making"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Experience"
                name="experience"
                value={form.experience}
                onChange={handleFormChange}
                required
                placeholder="e.g. 5 years"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {selectedKarigar ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default KarigarList; 